import { useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, Plus, Trash2, MessageSquare, Image as ImageIcon, ListOrdered, UtensilsCrossed } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  smallTextSize,
  baseTextSize,
  tertiaryText,
  semiBoldText,
} from '../../../styling/stylingHelper';
import { RecipeComponent, RecipeStep, getTranslatedText, SupportedLanguage } from '../../../services/recipes/recipesService';
import { RecipeNote } from '../../../services/recipeNotes/recipeNotesService';
import { useTeamMembersStore } from '../../../stores/appTeamMembersStore';
import { useTranslation } from 'react-i18next';
import { SortableList } from '../../common/SortableList';

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 1) return date.toLocaleDateString();
  if (diffDays === 1) return 'Yesterday';
  if (diffHours >= 1) return `${diffHours}h ago`;
  if (diffMins >= 1) return `${diffMins}m ago`;
  return 'Just now';
}

interface SortableStepRowProps {
  step: RecipeStep;
  index: number;
  title: string;
  description: string;
  draggable: boolean;
}

const SortableStepRow = ({ step, index, title, description, draggable }: SortableStepRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id || `step-${index}`,
    disabled: !draggable,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex gap-3 p-3 rounded-lg",
        "bg-[#F9FAFB] dark:bg-zinc-800/50",
        "border border-transparent dark:border-transparent"
      )}
    >
      {draggable && (
        <button
          {...attributes}
          {...listeners}
          className="touch-none cursor-grab active:cursor-grabbing mt-0.5 flex-shrink-0"
          aria-label="Drag to reorder"
          type="button"
        >
          <GripVertical className="w-4 h-4 text-[#98A2B3] dark:text-zinc-500" />
        </button>
      )}
      <div className={cn(
        "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
        "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white text-xs font-semibold"
      )}>
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        {title && (
          <p className={cn(smallTextSize, semiBoldText, primaryText, 'mb-1')}>
            {title}
          </p>
        )}
        <p className={cn(smallTextSize, secondaryText)}>{description}</p>
      </div>
    </div>
  );
};

interface RecipeComponentToggleProps {
  component: RecipeComponent;
  componentIndex: number;
  onReorderSteps?: (componentIndex: number, reorderedSteps: RecipeStep[]) => Promise<void>;
  // Notes props
  notes?: RecipeNote[];
  currentUserId?: string;
  currentUserRole?: 'admin' | 'staff';
  onAddNote?: () => void;
  onDeleteNote?: (noteId: string) => void;
  deletingNoteId?: string | null;
}

const RecipeComponentToggle = ({
  component,
  componentIndex,
  onReorderSteps,
  notes = [],
  currentUserId = '',
  currentUserRole = 'staff',
  onAddNote,
  onDeleteNote,
  deletingNoteId,
}: RecipeComponentToggleProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = (['en', 'es', 'nl'].includes(i18n.language) ? i18n.language : 'en') as SupportedLanguage;
  const getUserDisplayInfo = useTeamMembersStore(s => s.getUserDisplayInfo);

  const hasImages = component.images && component.images.length > 0;

  // Sort steps by position
  const sortedSteps = [...(component.steps || [])].sort((a, b) => {
    const posA = a.position !== undefined ? a.position : Infinity;
    const posB = b.position !== undefined ? b.position : Infinity;
    return posA - posB;
  });

  const handleStepsReorder = async (reordered: RecipeStep[]) => {
    if (!onReorderSteps) return;
    const updated = reordered.map((step, index) => ({ ...step, position: index }));
    try {
      await onReorderSteps(componentIndex, updated);
    } catch (error) {
      console.error('Failed to reorder steps:', error);
    }
  };

  return (
    <div className={cn(
      "bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark",
      "overflow-hidden transition-shadow",
      isExpanded && "shadow-sm"
    )}>
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full p-4 pl-12 flex items-center justify-between",
          "hover:bg-[#F9FAFB] dark:hover:bg-zinc-800/50 transition-colors",
          isExpanded ? "rounded-t-xl" : "rounded-xl"
        )}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h3 className={cn(baseTextSize, semiBoldText, primaryText, 'truncate')}>
            {getTranslatedText(component.nameTranslations, lang)}
            {getTranslatedText(component.amountTranslations, lang) && (
              <span className={cn('ml-2 font-normal', smallTextSize, tertiaryText)}>
                ({getTranslatedText(component.amountTranslations, lang)})
              </span>
            )}
          </h3>
          {/* Quick stats */}
          <div className="hidden min-[650px]:flex items-center gap-3">
            {component.ingredients && component.ingredients.length > 0 && (
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs",
                "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark"
              )}>
                <UtensilsCrossed className="w-3 h-3" />
                {component.ingredients.length}
              </span>
            )}
            {sortedSteps.length > 0 && (
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs",
                "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark"
              )}>
                <ListOrdered className="w-3 h-3" />
                {sortedSteps.length}
              </span>
            )}
            {notes.length > 0 && (
              <span className={cn(
                "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs",
                "bg-[#FEF3C7] dark:bg-yellow-900/20 text-[#B45309] dark:text-yellow-400"
              )}>
                <MessageSquare className="w-3 h-3" />
                {notes.length}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 pr-16">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#98A2B3] dark:text-zinc-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#98A2B3] dark:text-zinc-500" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-5 border-t border-[#E5E7EB] dark:border-borderPrimary-dark pt-4">
          {/* Component Images */}
          {hasImages && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
                <h4 className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('recipes.images')}
                </h4>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {component.images!.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${getTranslatedText(component.nameTranslations, lang)} - ${index + 1}`}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0 border border-[#E5E7EB] dark:border-zinc-700"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Ingredients */}
          {component.ingredients && component.ingredients.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <UtensilsCrossed className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
                <h4 className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('recipes.ingredients')}
                </h4>
                <span className={cn(
                  "px-2 py-0.5 rounded-md text-xs",
                  "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark"
                )}>
                  {component.ingredients.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {component.ingredients.map((ingredient, index) => {
                  const amount = getTranslatedText(ingredient.amountTranslations, lang);
                  const name = getTranslatedText(ingredient.nameTranslations, lang);
                  return (
                    <div
                      key={index}
                      className={cn(
                        "flex items-center gap-2 px-3 py-2 rounded-lg",
                        "bg-[#F9FAFB] dark:bg-zinc-800/50"
                      )}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0E8965] dark:bg-[#0E8965]" />
                      <span className={cn(smallTextSize, secondaryText)}>
                        {amount ? (
                          <>
                            <span className="font-medium text-[#344054] dark:text-textSecondary-dark">{amount}</span>
                            {' '}
                          </>
                        ) : null}
                        {name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Steps */}
          {sortedSteps.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ListOrdered className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
                <h4 className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('recipes.steps')}
                </h4>
                <span className={cn(
                  "px-2 py-0.5 rounded-md text-xs",
                  "bg-[#F2F4F7] dark:bg-zinc-800 text-[#475467] dark:text-textTertiary-dark"
                )}>
                  {sortedSteps.length}
                </span>
              </div>
              {onReorderSteps ? (
                <SortableList
                  items={sortedSteps}
                  getId={(step) => step.id || `step-${sortedSteps.indexOf(step)}`}
                  onReorder={handleStepsReorder}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <div className="space-y-2">
                    {sortedSteps.map((step, index) => (
                      <SortableStepRow
                        key={step.id || index}
                        step={step}
                        index={index}
                        title={getTranslatedText(step.titleTranslations, lang)}
                        description={getTranslatedText(step.descriptionTranslations, lang)}
                        draggable={true}
                      />
                    ))}
                  </div>
                </SortableList>
              ) : (
                <div className="space-y-2">
                  {sortedSteps.map((step, index) => (
                    <SortableStepRow
                      key={step.id || index}
                      step={step}
                      index={index}
                      title={getTranslatedText(step.titleTranslations, lang)}
                      description={getTranslatedText(step.descriptionTranslations, lang)}
                      draggable={false}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {(notes.length > 0 || onAddNote) && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#667085] dark:text-textTertiary-dark" />
                  <h4 className={cn(smallTextSize, semiBoldText, primaryText)}>
                    {t('recipes.notes')}
                  </h4>
                  {notes.length > 0 && (
                    <span className={cn(
                      "px-2 py-0.5 rounded-md text-xs",
                      "bg-[#FEF3C7] dark:bg-yellow-900/20 text-[#B45309] dark:text-yellow-400"
                    )}>
                      {notes.length}
                    </span>
                  )}
                </div>
                {onAddNote && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddNote();
                    }}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-lg",
                      "text-[#344054] dark:text-textSecondary-dark",
                      "hover:bg-[#F2F4F7] dark:hover:bg-zinc-800 transition-colors",
                      "text-sm font-medium"
                    )}
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('recipes.addNote')}</span>
                  </button>
                )}
              </div>
              {notes.length === 0 ? (
                <p className={cn(smallTextSize, tertiaryText)}>{t('recipes.noNotes')}</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((note) => {
                    const userInfo = getUserDisplayInfo(note.userId);
                    return (
                      <div
                        key={note.id}
                        className={cn(
                          "bg-[#FFFBEB] dark:bg-yellow-900/10 border border-[#FEF3C7] dark:border-yellow-900/20 rounded-lg p-3"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            {/* User info row */}
                            <div className={cn('flex items-center gap-2 mb-2', smallTextSize)}>
                              {/* User profile picture */}
                              {userInfo.profilePictureUrl ? (
                                <img
                                  src={userInfo.profilePictureUrl}
                                  alt={userInfo.displayName}
                                  className="w-5 h-5 rounded-full object-cover flex-shrink-0 border border-white dark:border-card-dark shadow-sm"
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-gray-400 dark:bg-backgroundSecondary-dark flex items-center justify-center flex-shrink-0">
                                  <span className="text-[10px] font-bold text-white dark:text-white">
                                    {userInfo.displayName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              {/* User name */}
                              <span className="font-medium text-[#344054] dark:text-textSecondary-dark truncate">
                                {userInfo.displayName}
                              </span>
                              <span className="text-[#D0D5DD] dark:text-zinc-600">·</span>
                              {/* Timestamp */}
                              <span className="text-[#667085] dark:text-textTertiary-dark">
                                {formatRelativeTime(note.createdAt)}
                              </span>
                            </div>
                            <p className={cn(smallTextSize, 'text-[#344054] dark:text-textSecondary-dark whitespace-pre-wrap break-words')}>
                              {note.content}
                            </p>
                          </div>
                          {onDeleteNote && (currentUserRole === 'admin' || note.userId === currentUserId) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteNote(note.id);
                              }}
                              disabled={deletingNoteId === note.id}
                              className={cn(
                                "flex-none p-1.5 rounded-lg",
                                "text-[#B42318] dark:text-red-400",
                                "hover:bg-[#FEF3F2] dark:hover:bg-red-900/20 transition-colors",
                                "disabled:opacity-50"
                              )}
                              title={t('recipes.deleteNote')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Empty state if component has no content */}
          {!hasImages &&
            (!component.ingredients || component.ingredients.length === 0) &&
            (!component.steps || component.steps.length === 0) &&
            (!component.notes || component.notes.length === 0) && (
              <p className={cn(smallTextSize, tertiaryText, 'italic text-center py-4')}>
                {t('recipes.noComponentData')}
              </p>
            )}
        </div>
      )}
    </div>
  );
};

export default RecipeComponentToggle;
